import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Loader2,
  Users,
  Home,
  Building2,
  Heart,
  Zap,
  ChevronRight,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';

const MILESTONES = [
  'Lead Captured',
  'Qualified',
  'Proposal Sent',
  'Contract Signed',
  'Site Survey Completed',
  'Installation Scheduled',
  'Installation Completed',
  'PTO / Project Complete',
  'Sale Completed',
];

const MILESTONE_COLORS = {
  'Lead Captured': { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-300' },
  'Qualified': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300' },
  'Proposal Sent': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300' },
  'Contract Signed': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300' },
  'Site Survey Completed': { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-300' },
  'Installation Scheduled': { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300' },
  'Installation Completed': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300' },
  'PTO / Project Complete': { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', border: 'border-green-300' },
  'Sale Completed': { bg: 'bg-emerald-600', text: 'text-white', border: 'border-emerald-600' },
};

const Pipeline = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingLead, setUpdatingLead] = useState(null);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const getLeadsByMilestone = (milestone) => {
    return leads.filter(lead => lead.status === milestone);
  };

  const handleMoveNext = async (lead) => {
    const currentIndex = MILESTONES.indexOf(lead.status);
    if (currentIndex === MILESTONES.length - 1) return;
    if (lead.payout_locked) {
      toast.error('Cannot change milestone - payout is locked');
      return;
    }

    const nextMilestone = MILESTONES[currentIndex + 1];
    setUpdatingLead(lead.id);
    
    try {
      await leadsAPI.update(lead.id, { status: nextMilestone });
      setLeads(prev => prev.map(l => 
        l.id === lead.id ? { ...l, status: nextMilestone } : l
      ));
      toast.success(`Moved to "${nextMilestone}"`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingLead(null);
    }
  };

  const handleMovePrev = async (lead) => {
    const currentIndex = MILESTONES.indexOf(lead.status);
    if (currentIndex === 0) return;
    if (lead.payout_locked) {
      toast.error('Cannot change milestone - payout is locked');
      return;
    }

    const prevMilestone = MILESTONES[currentIndex - 1];
    setUpdatingLead(lead.id);
    
    try {
      await leadsAPI.update(lead.id, { status: prevMilestone });
      setLeads(prev => prev.map(l => 
        l.id === lead.id ? { ...l, status: prevMilestone } : l
      ));
      toast.success(`Moved back to "${prevMilestone}"`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingLead(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Deal Pipeline
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Track and manage deal progress through milestones
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>{leads.length} total deals</span>
        </div>
      </div>

      {/* Pipeline Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {MILESTONES.map((milestone) => {
            const milestoneLeads = getLeadsByMilestone(milestone);
            const colors = MILESTONE_COLORS[milestone];
            
            return (
              <div
                key={milestone}
                className="w-80 flex-shrink-0"
                data-testid={`pipeline-column-${milestone.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Card className={`${colors.bg} border-t-4 ${colors.border}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className={`flex items-center justify-between text-base ${colors.text}`}>
                      <span className="font-heading">{milestone}</span>
                      <Badge variant="secondary" className="text-xs">
                        {milestoneLeads.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ScrollArea className="h-[500px] pr-2">
                      <div className="space-y-2">
                        {milestoneLeads.map((lead) => (
                          <Card
                            key={lead.id}
                            className="lead-card group"
                            data-testid={`pipeline-lead-${lead.id}`}
                          >
                            <div
                              className="cursor-pointer"
                              onClick={() => navigate(`/leads/${lead.id}`)}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  {lead.lead_type === 'Commercial' ? (
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <Home className="w-4 h-4 text-slate-400" />
                                  )}
                                  <span className="font-medium text-slate-900 dark:text-white text-sm">
                                    {lead.customer_name}
                                  </span>
                                </div>
                                {lead.nonprofit_eligible && (
                                  <Heart className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                                {lead.city}, {lead.state}
                              </p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1 text-xs">
                                  <Zap className="w-3 h-3 text-amber-500" />
                                  <span className="font-mono font-medium">
                                    {lead.system_size_kw} kW
                                  </span>
                                </div>
                                <span className="text-xs font-mono text-emerald-600 font-medium">
                                  ${lead.commission_rep_amount.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            
                            {/* Quick actions */}
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMovePrev(lead);
                                }}
                                disabled={
                                  MILESTONES.indexOf(lead.status) === 0 ||
                                  updatingLead === lead.id ||
                                  lead.payout_locked
                                }
                                data-testid={`move-prev-${lead.id}`}
                              >
                                ← Back
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-emerald-600 hover:text-emerald-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveNext(lead);
                                }}
                                disabled={
                                  MILESTONES.indexOf(lead.status) === MILESTONES.length - 1 ||
                                  updatingLead === lead.id ||
                                  lead.payout_locked
                                }
                                data-testid={`move-next-${lead.id}`}
                              >
                                {updatingLead === lead.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>Next →</>
                                )}
                              </Button>
                            </div>
                          </Card>
                        ))}
                        
                        {milestoneLeads.length === 0 && (
                          <div className="text-center py-8 text-slate-400 text-sm">
                            No deals
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Pipeline Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {MILESTONES.slice(0, 5).map((milestone) => {
              const count = getLeadsByMilestone(milestone).length;
              const totalKw = getLeadsByMilestone(milestone).reduce((sum, l) => sum + l.system_size_kw, 0);
              const colors = MILESTONE_COLORS[milestone];
              
              return (
                <div
                  key={milestone}
                  className={`p-4 rounded-lg ${colors.bg}`}
                >
                  <p className={`text-xs font-medium ${colors.text} mb-1`}>
                    {milestone}
                  </p>
                  <p className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                    {count}
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-1">
                    {totalKw.toFixed(1)} kW
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Pipeline;
